import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Columns, Copy, Eye, PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useSharedSections } from "@/lib/useSharedSections";
import { AdminModal, BTN_BLACK, BTN_WHITE, errText } from "@/components/pageSettings/AdminModal";
import { DesignBuilder } from "./DesignBuilder";
import { DesignPreview, sampleCtx } from "./designPreview";
import { CLASSIC_URL, TemplateCard, TestBadge, TestPick } from "./TemplateGallery";
import { DesignCompare } from "./DesignCompare";
import { GalleryTestBar, LIVE } from "./GalleryTestBar";

const SMALL = `${BTN_WHITE} !px-2 !py-1`;

function DesignCard({ d, labels, isDefault, onPreview, onCopy, onEdit, onDelete, onDefault, picked, onPick, pickDisabled, testRow, testMin }) {
  const tid = d.key.replace(":", "-");
  return (
    <div className={`flex flex-col gap-2 border-2 p-3 ${picked ? "border-emerald-600 ring-2 ring-emerald-600" : "border-black"} ${isDefault ? "bg-emerald-50" : "bg-white"}`} data-testid={`ty-design-card-${tid}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-chivo font-black leading-tight">{d.name}</span>
        {isDefault && <span className="border border-black bg-emerald-600 px-1 text-[9px] uppercase tracking-widest text-white" data-testid={`ty-design-default-chip-${tid}`}>Default</span>}
        <TestBadge tid={tid} row={testRow} minViews={testMin} />
        <TestPick tid={tid} picked={picked} onPick={onPick} disabled={pickDisabled} />
      </div>
      {d.description && <p className="text-[11px] leading-snug text-zinc-600">{d.description}</p>}
      <div className="flex flex-wrap gap-1">
        {(d.blocks || []).filter((b) => b.enabled !== false).map((b, i) => (
          <span key={i} className="border border-zinc-400 px-1 text-[9px] uppercase tracking-widest text-zinc-700">{labels[b.type] || b.type}</span>
        ))}
      </div>
      <div className="mt-auto flex flex-wrap gap-1.5 pt-1">
        {onPreview && <button type="button" className={SMALL} onClick={onPreview} data-testid={`ty-design-preview-${tid}`}><Eye size={11} /> Preview</button>}
        {onEdit && <button type="button" className={SMALL} onClick={onEdit} data-testid={`ty-design-edit-${tid}`}><PencilSimple size={11} /> Edit</button>}
        {onCopy && <button type="button" className={SMALL} onClick={onCopy} data-testid={`ty-design-copy-${tid}`}><Copy size={11} /> {onEdit ? "Duplicate" : "Customize a copy"}</button>}
        {!isDefault && <button type="button" className={SMALL} onClick={onDefault} data-testid={`ty-design-make-default-${tid}`}>Make default</button>}
        {onDelete && <button type="button" className={`${SMALL} hover:!bg-red-600`} onClick={onDelete} data-testid={`ty-design-delete-${tid}`}><Trash size={11} /></button>}
      </div>
    </div>
  );
}

export function DesignsManager({ onClose }) {
  const shared = useSharedSections();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [picked, setPicked] = useState([]);
  const [test, setTest] = useState(null);
  const loadTest = useCallback(() => {
    api.get("/admin/thankyou-test").then((r) => setTest(r.data)).catch(() => setTest(null));
  }, []);
  useEffect(() => { loadTest(); }, [loadTest]);
  const inTest = LIVE.includes(test?.status) ? Object.fromEntries((test.standings || []).map((r) => [r.key, r])) : {};
  const pickProps = (key) => ({
    testRow: inTest[key],
    testMin: test?.min_views,
    picked: picked.includes(key),
    pickDisabled: !picked.includes(key) && picked.length >= 4,
    onPick: () => setPicked((x) => (x.includes(key) ? x.filter((k) => k !== key) : x.length >= 4 ? x : [...x, key])),
  });
  const load = useCallback(() => {
    api.get("/admin/thankyou-designs").then((r) => setData(r.data)).catch((e) => toast.error(errText(e, "Couldn't load designs")));
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok, fail) => {
    try { await fn(); if (ok) toast.success(ok); load(); } catch (e) { toast.error(errText(e, fail)); }
  };
  const create = (copyFrom, name) => run(async () => {
    const r = await api.post("/admin/thankyou-designs", { name, copy_from: copyFrom });
    setEditing(r.data);
  }, null, "Couldn't create the design");
  const makeDefault = (key) => run(() => api.put("/admin/thankyou-designs-default", { design: key }), "Default thank-you design updated", "Couldn't set the default");
  const remove = (d) => {
    if (!window.confirm(`Delete "${d.name}"? Pages using it go back to their default design.`)) return;
    run(() => api.delete(`/admin/thankyou-designs/${d.id}`), "Design deleted", "Couldn't delete the design");
  };
  const reset = (t) => {
    if (!window.confirm(`Put "${t.name}" back to how it came? Your changes to it will be lost.`)) return;
    run(() => api.delete(`/admin/thankyou-templates/${t.template}`), `${t.name} is back to the original`, "Couldn't reset the design");
  };
  const editTemplate = (t) => setEditing({ template: t.template, name: t.name, blocks: t.edited ? t.blocks : t.start_blocks });

  if (editing && data) {
    return <DesignBuilder design={editing} labels={data.block_labels} onBack={() => { setEditing(null); load(); }} onClose={onClose} />;
  }
  const card = (d, own) => (
    <DesignCard key={d.key} d={d} labels={data.block_labels} isDefault={data.default === d.key}
                onPreview={d.blocks?.length ? () => setPreview(d) : null}
                onCopy={d.blocks?.length ? () => create(d.key, `${d.name} copy`.slice(0, 60)) : null}
                onEdit={own ? () => setEditing(d) : null} onDelete={own ? () => remove(d) : null}
                onDefault={() => makeDefault(d.key)} {...pickProps(d.key)} />
  );
  return (
    <AdminModal eyebrow="Thank-you pages" title="Thank-you designs" onClose={onClose} testId="ty-designs-modal" wide
                footer={<button type="button" className={BTN_BLACK} onClick={onClose} data-testid="ty-designs-done">Done</button>}>
      {!data ? <div className="label-mono py-4">Loading…</div> : (
        <>
          <p className="mb-3 text-[11px] text-zinc-600">
            The default design is used by every funnel and buyer that hasn&apos;t picked its own. Pick a design for one
            funnel or buyer from its &ldquo;Thank-you page&rdquo; button. Every design shows next steps and who will contact the visitor.
          </p>
          <div className="flex items-center gap-2">
            <div className="label-mono">Ready-made designs</div>
            <button type="button" className={`${BTN_BLACK} ml-auto`} onClick={() => setComparing(true)} data-testid="ty-compare-open">
              <Columns size={12} weight="bold" /> Compare side by side
            </button>
          </div>
          <p className="mt-1 text-[11px] text-zinc-600">Click a page to see it full size. Edit changes the design for every page that uses it. Tick <b>Test</b> on 2–4 designs to start a design test.</p>
          <div className="mt-2 grid gap-4 md:grid-cols-2" data-testid="ty-designs-templates">
            {data.templates.map((t) => (
              <TemplateCard key={t.key} t={t} shared={shared} isDefault={data.default === t.key}
                            onPreview={() => setPreview(t)} onEdit={() => editTemplate(t)} onReset={() => reset(t)}
                            onCopy={() => create(t.key, `${t.name} copy`.slice(0, 60))} onDefault={() => makeDefault(t.key)} {...pickProps(t.key)} />
            ))}
          </div>
          <div className="mt-6 flex items-center gap-2">
            <div className="label-mono">Your designs</div>
            <button type="button" className={`${BTN_WHITE} ml-auto`} onClick={() => create(null, "New design")} data-testid="ty-designs-new">
              <Plus size={11} weight="bold" /> New design
            </button>
          </div>
          {data.designs.length === 0 ? (
            <div className="mt-2 border border-dashed border-zinc-400 p-4 text-[11px] text-zinc-600" data-testid="ty-designs-empty">
              No designs of your own yet. Use &ldquo;Customize a copy&rdquo; on a ready-made design to start.
            </div>
          ) : (
            <div className="mt-2 grid gap-3 md:grid-cols-2 lg:grid-cols-4" data-testid="ty-designs-own">{data.designs.map((d) => card(d, true))}</div>
          )}
          {picked.length > 0 && (
            <GalleryTestBar picked={picked} onClear={() => setPicked([])} test={test} reloadTest={loadTest}
                            names={Object.fromEntries([...data.templates, ...data.designs].map((x) => [x.key, x.name]))} />
          )}
        </>
      )}
      {preview && (
        <AdminModal eyebrow="Preview" title={preview.name} onClose={() => setPreview(null)} testId="ty-design-preview-modal" wide>
          {preview.blocks?.length ? <DesignPreview blocks={preview.blocks} ctx={sampleCtx(shared)} /> : (
            <iframe title="Classic thank-you page" src={CLASSIC_URL} className="h-[72vh] w-full border-2 border-black" data-testid="ty-classic-preview-frame" />
          )}
        </AdminModal>
      )}
      {comparing && data && (
        <DesignCompare data={data} shared={shared} onDefault={makeDefault} onClose={() => { setComparing(false); loadTest(); }} />
      )}
    </AdminModal>
  );
}
